import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { PrismicRichText, PrismicLink } from "@prismicio/react"

import ConditionalWrapper from "../ConditionalWrapper/ConditionalWrapper"
import { CTA_TEXT_DEFAULT } from "../../constants"

const LAYOUT_FULL = "Full"
const LAYOUT_BASIC = "Basic"
const LAYOUT_DESCRIPTION = "Description"

const BrandsSection = ({ fields, brands }) => {
  const { layout, title, content, cta_text, cta_link } = fields

  if (!brands.length) return null

  if (layout === LAYOUT_FULL) {
    return (
      <div className="grid grid-cols-12">
        <div className="section col-span-12 bg-night-black text-cloud-white md:col-span-6">
          <div className="lg:pr-32">
            <h2 className="mb-8 text-4xl font-semibold">{title}</h2>

            <div className="rte rte--large mb-10">
              <PrismicRichText field={content.richText} />
            </div>

            {cta_link && (
              <div>
                <PrismicLink className="button button--white" field={cta_link}>
                  {cta_text || CTA_TEXT_DEFAULT}
                </PrismicLink>
              </div>
            )}
          </div>
        </div>
        <div className="section col-span-12 bg-cloud-white md:col-span-6">
          <div className="flex h-full flex-col justify-center">
            <div className="space-y-12">
              {brands.map(item => {
                const { id, title, logo, website_link } =
                  item.brand.document.data
                if (!logo) return null
                return (
                  <div className="mx-auto h-24 w-56" key={`${title}-${id}`}>
                    <ConditionalWrapper
                      condition={website_link}
                      wrapper={children => (
                        <PrismicLink
                          className="block h-full"
                          field={website_link}
                          title={title}
                        >
                          {children}
                        </PrismicLink>
                      )}
                    >
                      <GatsbyImage
                        className="h-full w-full"
                        objectFit="contain"
                        image={getImage(logo.gatsbyImageData)}
                        alt={logo.alt || ""}
                      />
                    </ConditionalWrapper>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (layout === LAYOUT_BASIC) {
    return (
      <div className="bg-sand-yellow">
        <div className="section">
          <div className="grid grid-cols-12 lg:gap-x-10">
            <div className="col-start-1 col-end-13 mb-12 lg:mb-14">
              <h2 className="max-w-sm text-3xl font-bold uppercase tracking-wide">
                {title}
              </h2>
              {cta_link && (
                <PrismicLink
                  field={cta_link}
                  className="link link--black mt-6 text-lg font-semibold"
                >
                  {cta_text || CTA_TEXT_DEFAULT}
                </PrismicLink>
              )}
            </div>
          </div>

          <div className="grid grid-cols-12 lg:gap-x-10">
            <div className="col-start-3 col-end-13">
              <div className="grid grid-cols-10 gap-y-10 lg:gap-y-0 lg:gap-x-10">
                {brands.map(item => {
                  const { id, title, logo, website_link } =
                    item.brand.document.data

                  if (!logo) return null

                  return (
                    <div className="col-span-10 lg:col-span-3" key={id}>
                      <div className="mx-auto h-16 w-48">
                        <ConditionalWrapper
                          condition={website_link}
                          wrapper={children => (
                            <PrismicLink
                              className="block h-full"
                              field={website_link}
                              title={title}
                            >
                              {children}
                            </PrismicLink>
                          )}
                        >
                          <GatsbyImage
                            className="h-full w-full"
                            objectFit="contain"
                            image={getImage(logo.gatsbyImageData)}
                            alt={logo.alt || ""}
                          />
                        </ConditionalWrapper>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (layout === LAYOUT_DESCRIPTION) {
    const bgClasses = ["bg-sand-yellow bg-opacity-25", "bg-cloud-white"]
    if (brands.length % 2 === 0) bgClasses.reverse()

    return brands.map((item, index) => {
      const {
        id,
        logo,
        website_link,
        description,
        is_b_corporation_certified,
      } = item.brand.document.data
      const { image } = item

      if (!logo) return null

      const bgClassIndex = (index + 1) % 2 === 0 ? 1 : 0

      return (
        <div className={`section ${bgClasses[bgClassIndex]}`} key={id}>
          <div className="grid grid-cols-12 lg:gap-x-10">
            <div className="col-start-1 col-end-11 lg:col-start-3 lg:col-end-9">
              <ConditionalWrapper
                condition={is_b_corporation_certified}
                wrapper={children => (
                  <div className="flex items-end space-x-10">{children}</div>
                )}
              >
                <>
                  <GatsbyImage
                    className="max-w-56 max-h-24"
                    objectFit="contain"
                    image={getImage(logo.gatsbyImageData)}
                    alt={logo.alt || title}
                  />
                  {is_b_corporation_certified && (
                    <svg
                      className="h-16 fill-current text-night-black"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 254 390"
                    >
                      <path
                        fillRule="evenodd"
                        d="M29.9 10.2c.8.7 1.5 1.5 2.1 2.4.6.9.9 1.9 1.1 3h9.3c-.3-2.5-1-4.7-2.1-6.7C39.2 7 37.8 5.3 36.1 4c-1.6-1.3-3.6-2.3-5.7-3-2.1-.7-4.4-1-6.8-1-3.3 0-6.3.6-9 1.8-2.7 1.2-5 2.8-6.8 4.9C6 8.8 4.6 11.2 3.6 14s-1.5 5.8-1.5 9.1c0 3.2.5 6.2 1.5 8.9 1 2.8 2.4 5.2 4.2 7.2 1.9 2 4.1 3.6 6.8 4.8 2.6 1.1 5.7 1.7 9 1.7 2.7 0 5.2-.4 7.4-1.2 2.3-.8 4.2-2 5.9-3.5 1.6-1.5 3-3.4 4-5.6 1-2.2 1.7-4.7 1.9-7.4h-9.3c-.3 2.9-1.3 5.3-3 7.1-1.7 1.8-4 2.7-6.9 2.7-2.2 0-4-.5-5.5-1.3s-2.8-1.9-3.7-3.3c-.9-1.4-1.6-3-2-4.7-.4-1.8-.6-3.6-.6-5.4 0-2 .2-3.8.6-5.6.4-1.8 1.1-3.4 2-4.8 1-1.4 2.2-2.6 3.7-3.4 1.5-.9 3.3-1.3 5.5-1.3 1.2 0 2.3.2 3.4.6 1.1.4 2 .9 2.9 1.6ZM62 38.9c-2.4 0-4.3-.6-5.6-1.9-1.3-1.3-2-3.3-2.2-6.2H77c.2-2.4 0-4.8-.6-7s-1.5-4.2-2.8-6c-1.3-1.8-2.9-3.2-4.9-4.2-2-1.1-4.3-1.6-7-1.6-2.4 0-4.6.4-6.6 1.3-2 .8-3.7 2-5.1 3.5-1.4 1.5-2.5 3.3-3.3 5.3-.8 2-1.2 4.2-1.2 6.6 0 2.4.3 4.7 1.1 6.7.7 2.1 1.8 3.8 3.2 5.3 1.4 1.5 3.1 2.6 5.1 3.4 2 .8 4.2 1.2 6.7 1.2 3.6 0 6.7-.8 9.2-2.4 2.5-1.6 4.4-4.3 5.6-8.1h-7.6c-.3 1-1 1.9-2.3 2.8-1.2.9-2.7 1.3-4.5 1.3Zm-.4-20.2c2.1 0 3.7.6 4.7 1.7s1.7 2.8 2.1 5H54.3c0-.524.152-1.124.324-1.8l.076-.3c.3-.8.7-1.5 1.2-2.2.6-.6 1.3-1.2 2.2-1.7 1-.5 2.1-.7 3.5-.7ZM89.3 13v5.9h.1c.4-1 1-1.9 1.6-2.7.7-.8 1.5-1.5 2.4-2.1.9-.6 1.9-1 2.9-1.4 1-.3 2.1-.5 3.2-.5.6 0 1.2.1 1.9.3v8.1c-.4-.1-.9-.2-1.5-.2-.6-.1-1.1-.1-1.6-.1-1.6 0-2.9.3-4 .8s-2 1.3-2.7 2.2c-.7.9-1.2 2-1.4 3.2-.3 1.2-.4 2.5-.4 4v14.1h-8.7V13h8.2Zm35.3 5.8v-6h-6.4V3.3h-8.7v9.5h-5.3v5.8h5.3v18.7c0 1.5.3 2.8.8 3.8.6 1 1.3 1.8 2.2 2.3.9.6 2 .9 3.2 1.1 1.2.2 2.5.3 3.8.3.388 0 .8-.023 1.224-.048.45-.026.913-.052 1.376-.052.8 0 1.7-.1 2.4-.2v-6.7c-.4.1-.9.2-1.3.2-.4.1-.9.1-1.4.1-1.4 0-2.4-.2-2.9-.7-.5-.5-.7-1.4-.7-2.9V18.8h6.4ZM129 .9h8.7v7.2H129V.9Zm8.7 43.7H129V13h8.7v31.6Zm3.6-31.6v5.8h5.2v25.8h8.7V18.9h6v-5.8h-6v-1.9c0-1.3.3-2.3.8-2.8s1.3-.8 2.5-.8c1.1 0 2.2.1 3.2.2V1.3c-.4-.05-.775-.075-1.15-.1-.375-.025-.75-.05-1.15-.1-.8-.1-1.5-.1-2.3-.1-3.6 0-6.2.9-8 2.7-1.7 1.8-2.6 4.1-2.6 6.9V13h-5.2ZM166.4.9h8.7v7.2h-8.7V.9Zm8.7 43.7h-8.7V13h8.7v31.6Zm19.3-5.7c-2.4 0-4.3-.6-5.6-1.9-1.3-1.3-2-3.3-2.2-6.2h22.8c.2-2.4 0-4.8-.6-7s-1.5-4.2-2.8-6c-1.3-1.8-2.9-3.2-4.9-4.2-2-1.1-4.3-1.6-7-1.6-2.4 0-4.6.4-6.6 1.3-2 .8-3.7 2-5.1 3.5-1.4 1.5-2.5 3.3-3.3 5.3-.8 2-1.2 4.2-1.2 6.6 0 2.4.3 4.7 1.1 6.7.7 2.1 1.8 3.8 3.2 5.3 1.4 1.5 3.1 2.6 5.1 3.4 2 .8 4.2 1.2 6.7 1.2 3.6 0 6.7-.8 9.2-2.4 2.5-1.6 4.4-4.3 5.6-8.1h-7.6c-.3 1-1 1.9-2.3 2.8-1.2.9-2.7 1.3-4.5 1.3Zm-.5-20.2c2.1 0 3.7.6 4.7 1.7s1.8 2.8 2.1 5h-14.1c0-.524.152-1.124.324-1.8l.076-.3c.3-.8.6-1.5 1.2-2.2.5-.6 1.3-1.2 2.2-1.7 1-.5 2.1-.7 3.5-.7Zm41.9 21.8c-1 1.7-2.4 2.9-4 3.7-1.7.8-3.5 1.1-5.6 1.1-2.4 0-4.4-.5-6.2-1.4-1.8-.9-3.3-2.2-4.4-3.7-1.2-1.6-2-3.4-2.6-5.4-.6-2-.9-4.2-.9-6.4 0-2.1.3-4.2.9-6.1.6-2 1.5-3.7 2.6-5.2 1.2-1.5 2.6-2.7 4.4-3.6 1.8-.9 3.8-1.4 6.1-1.4 1.9 0 3.7.4 5.3 1.2 1.7.8 3 2 4 3.5h.1V.9h8.7v43.7h-8.3v-4h-.1v-.1Zm-.4-15.6c-.2-1.2-.7-2.3-1.3-3.2-.6-.9-1.4-1.7-2.3-2.2-.9-.6-2.1-.9-3.5-.9s-2.6.3-3.6.9c-1 .6-1.8 1.3-2.4 2.3-.6.9-1 2-1.3 3.2-.3 1.2-.4 2.5-.4 3.8 0 1.2.1 2.4.4 3.7.3 1.2.8 2.3 1.4 3.3.6 1 1.4 1.7 2.4 2.3 1 .6 2.1.9 3.5.9s2.6-.3 3.6-.9c1-.6 1.7-1.3 2.3-2.3.6-1 1-2 1.2-3.3.2-1.2.4-2.5.4-3.8 0-1.3-.1-2.6-.4-3.8Zm-113.7 90.7H71.1v139.6h67.4c27.7 0 42.4-19.6 42.4-40.1 0-17.2-9.4-30.1-26.6-33.6v-.4c12.9-5.3 20.7-16.5 20.7-31.3 0-13.7-7.2-23.6-16.4-29.1-8.4-5.1-24.6-5.1-36.9-5.1Zm5.2 60H89.7v-44.4h37.2c16 0 29.7 2.7 29.7 22.1 0 14.7-8.6 22.3-29.7 22.3Zm9.3 63.9H89.7v-48.3h42.4c15.6 0 30.3 5.1 30.3 23.3 0 15.2-10.2 25-26.2 25Zm92.2-54.1c0 58.6-47.5 106.1-106.2 106.1-58.6 0-106.1-47.5-106.1-106.1 0-58.6 47.5-106.1 106.1-106.1 58.7 0 106.2 47.5 106.2 106.1ZM122.3 63.1C54.8 63.1 0 117.8 0 185.4c0 67.5 54.7 122.3 122.3 122.3 67.5 0 122.3-54.7 122.3-122.3S189.8 63.1 122.3 63.1ZM15.8 328.6c3.4 0 6.4 2.3 6.8 5.4v-.1h6.5c-.8-7.1-6.5-11.1-13.3-11.1-9.6 0-15.2 7.1-15.2 16.3 0 9 5.6 16.1 15.2 16.1 7.6 0 12.9-4.9 13.6-12.6h-6.6c-.5 4-2.8 6.9-7 6.9-6.1 0-8.4-5.2-8.4-10.3 0-5.4 2.3-10.6 8.4-10.6Zm27.8 3c7.1 0 11.6 4.7 11.6 11.8 0 7.1-4.5 11.8-11.6 11.8-7 0-11.6-4.7-11.6-11.8.1-7.2 4.6-11.8 11.6-11.8Zm0 18.9c4.2 0 5.5-3.6 5.5-7.1 0-3.6-1.3-7.2-5.5-7.2s-5.4 3.6-5.4 7.2c0 3.5 1.3 7.1 5.4 7.1Zm21-14.1v-4.2H58.7v22.4h6.1v-10.1c0-4 1.6-7.2 6.1-7.2.7 0 1.6.1 2.2.2v-5.7c-.4-.1-.9-.2-1.3-.2-2.9 0-6 2-7.1 4.8h-.1Zm11.9-4.2h5.8v2.9h.1c1.5-2.4 3.9-3.5 6.7-3.5 7 0 10.2 5.7 10.2 12 0 6-3.3 11.6-9.8 11.6-2.7 0-5.2-1.2-6.7-3.4h-.2v10.7h-6.1v-30.3Zm16.6 11.2c0-3.6-1.4-7.2-5.4-7.2s-5.3 3.6-5.3 7.2 1.4 7.1 5.4 7.1 5.3-3.4 5.3-7.1Zm32.3 0c0-7.1-4.5-11.8-11.6-11.8-7 0-11.6 4.6-11.6 11.8 0 7.1 4.6 11.8 11.6 11.8s11.6-4.7 11.6-11.8Zm-6.1 0c0 3.5-1.3 7.1-5.5 7.1s-5.4-3.6-5.4-7.1c0-3.6 1.2-7.2 5.4-7.2s5.5 3.6 5.5 7.2Zm10.2-11.2h5.8v4.2h.1c1.1-2.8 4.2-4.8 7.1-4.8.4 0 1 .1 1.3.2v5.7c-.6-.1-1.5-.2-2.2-.2-4.5 0-6.1 3.2-6.1 7.2v10.1h-6.1v-22.4h.1Zm27.1-.6c-5 0-10.1 1.7-10.5 7.5v.1h6.2c.2-2.5 1.8-3.4 4.1-3.4 2.2 0 4 .4 4 3 0 2.007-2.256 2.274-5.112 2.612h-.007c-.699.083-1.434.17-2.181.288-3.8.5-7.6 1.7-7.6 6.8 0 4.6 3.4 6.7 7.6 6.7 2.7 0 5.6-.8 7.5-2.8 0 .8.2 1.5.4 2.2h6.2c-.6-.9-.8-3-.8-5V338c0-5.4-5.3-6.4-9.8-6.4Zm-.236 13.136c1.568-.208 3.136-.416 3.936-1.136h.1v2.4c0 1.3-.2 5.1-5 5.1-2 0-3.8-.6-3.8-2.9s1.7-2.9 3.6-3.3c.38-.06.772-.112 1.164-.164ZM179 332.2h4.5v4.1H179v11.1c0 2.1.5 2.6 2.6 2.6.7 0 1.3 0 1.9-.2v4.8c-1 .2-2.4.2-3.6.2-3.8 0-7-.9-7-5.3v-13.2h-3.7v-4.1h3.7v-6.7h6.1v6.7Zm7.6-3.5h6.1v-5.1h-6.1v5.1Zm6.1 3.5h-6.1v22.4h6.1v-22.4Zm14.9-.6c7.1 0 11.6 4.7 11.6 11.8 0 7.1-4.6 11.8-11.6 11.8s-11.5-4.7-11.5-11.8c-.1-7.2 4.5-11.8 11.5-11.8Zm0 18.9c4.2 0 5.5-3.6 5.5-7.1 0-3.6-1.2-7.2-5.5-7.2-4.2 0-5.4 3.6-5.4 7.2 0 3.5 1.2 7.1 5.4 7.1Zm21.3-18.3h-5.8v22.4h6.1v-11.7c0-4.5 1.5-6.4 4.8-6.4 2.8 0 3.9 1.8 3.9 5.5v12.6h6.1v-13.8c0-5.5-1.7-9.2-8.2-9.2-2.6 0-5.2 1.2-6.8 3.7h-.1v-3.1Zm15.3 41.4H.5v15.6h243.7v-15.6Zm2.1-50.9h1.5v-.6h-3.6v.6h1.4v3.9h.7v-3.9Zm6.7 3.9H253.8v-4.5h-1.2l-1.3 3.4-1.4-3.4h-1.2v4.5h.8v-3.8l1.5 3.8h.5l1.5-3.8v3.8Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </>
              </ConditionalWrapper>
              {description && (
                <div className="rte rte--large mt-10">
                  <PrismicRichText field={description.richText} />
                </div>
              )}
              {website_link && (
                <PrismicLink
                  className="link link--black mt-6 text-lg font-semibold"
                  field={website_link}
                  title={title}
                >
                  Visit {title} website
                </PrismicLink>
              )}
            </div>

            {image?.gatsbyImageData && (
              <div className="col-start-1 col-end-13 mt-12 lg:col-start-3 lg:col-end-10">
                <GatsbyImage
                  image={getImage(image.gatsbyImageData)}
                  alt={image.alt || ""}
                />
              </div>
            )}
          </div>
        </div>
      )
    })
  }
}

export default BrandsSection
